import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, Copy, Plus, X } from "lucide-react";
import { apiGet, apiPut } from "../../utils/api";
import { getRole } from "../../utils/authService";
import { toast } from "../../utils/toast";
import "./sso-settings.css";

const MANAGE_ROLES = ["admin", "client", "ceo", "cfo"];
// Platform "admin" is never here — it's cross-tenant, so no SSO path may grant it. "client" skips
// the approval step entirely once mapped (writing the row is the review). "employee"/"manager"
// land in an "awaiting allocation" state until a tenant admin links an Agent/department — still
// listed here since it's a deliberate, reviewable outcome either way.
const ROLE_OPTIONS = [
  { value: "client", label: "Client (tenant owner)" },
  { value: "ceo", label: "CEO" },
  { value: "cfo", label: "CFO" },
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
];

const DEFAULT_CONFIG = {
  protocol: "saml",
  enabled: false,
  idpEntityId: "",
  idpSsoUrl: "",
  idpCertificateMasked: "",
  attributeMapping: { email: "email", name: "name", role: "", group: "" },
  requireSso: false,
  roleMapping: [],
  departmentMapping: [],
  spEntityId: "",
  spMetadataUrl: "",
  acsUrl: "",
  oidc: {
    issuer: "",
    clientId: "",
    clientSecretMasked: "",
    scopes: "openid profile email",
    tokenAuthMethod: "client_secret_basic",
    claimMapping: { email: "email", name: "name", role: "", group: "" },
  },
  oidcRedirectUri: "",
  oidcLoginUrl: "",
};

function copyToClipboard(value) {
  navigator.clipboard?.writeText(value).then(
    () => toast.success("Copied."),
    () => toast.error("Could not copy — select and copy manually.")
  );
}

export default function SsoSettings() {
  const role = useMemo(() => getRole(), []);
  const canManage = MANAGE_ROLES.includes(role);

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [departments, setDepartments] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  // Default-seeded templates ("Employee", "Manager", "CEO", "CFO") share a name with a built-in
  // ROLE_OPTIONS entry — listing them too would just show the same role twice under two
  // different values. "Download" is the seeded installer-only login, never a real role someone
  // signs into via SSO. Excluded by name (not derived role) so a genuinely custom template that
  // happens to derive to, say, "cfo" via different views still shows up as its own distinct option.
  const EXCLUDED_TEMPLATE_NAMES = useMemo(
    () => new Set([...ROLE_OPTIONS.map((r) => r.label.toLowerCase()), "download"]),
    []
  );
  const selectableRoleTemplates = useMemo(
    () => roleTemplates.filter((t) => !EXCLUDED_TEMPLATE_NAMES.has(String(t.name || "").trim().toLowerCase())),
    [roleTemplates, EXCLUDED_TEMPLATE_NAMES]
  );
  const [idpCert, setIdpCert] = useState("");
  const [oidcClientSecret, setOidcClientSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchConfig = useCallback(async () => {
    try {
      const res = await apiGet("/api/sso-config");
      const data = await res.json().catch(() => null);
      if (data?.success) setConfig(data);
    } catch {
      setError("Could not load SSO settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Populates the "Department" dropdown in the department-mapping table below — the tenant's
  // real teams, same list RoleAccounts.jsx picks a manager's department from manually.
  const fetchDepartments = useCallback(async () => {
    try {
      const res = await apiGet("/api/departments");
      const data = await res.json().catch(() => null);
      if (data?.success) setDepartments(data.departments || []);
    } catch {
      // Non-fatal — the department-mapping table just shows an empty dropdown if this fails.
    }
  }, []);

  // Populates the "Custom roles" group in the Role mapping dropdown below — the tenant's own
  // role templates (RoleAccounts.jsx), so an IdP claim can map straight to one instead of only
  // the 5 built-in roles. A ceo/cfo admin without the "productivity_config" view may get a 403
  // here (account-roles is gated tighter than this page) — non-fatal, they just see the
  // built-in options only, same graceful-degradation pattern as fetchDepartments above.
  const fetchRoleTemplates = useCallback(async () => {
    try {
      const res = await apiGet("/api/users/account-roles");
      const data = await res.json().catch(() => null);
      if (data?.success) setRoleTemplates(data.roles || []);
    } catch {
      // Non-fatal — the dropdown just shows built-in roles only if this fails.
    }
  }, []);

  useEffect(() => {
    void fetchConfig();
    void fetchDepartments();
    void fetchRoleTemplates();
  }, [fetchConfig, fetchDepartments, fetchRoleTemplates]);

  const save = useCallback((overrides = {}) => {
    setSaving(true);
    setError("");
    setSuccessMsg("");
    const next = { ...config, ...overrides };
    return apiPut("/api/sso-config", {
      protocol: next.protocol,
      enabled: next.enabled,
      idpEntityId: next.idpEntityId,
      idpSsoUrl: next.idpSsoUrl,
      idpCertificate: idpCert.trim(), // only sent if the admin pasted a new one
      attributeMapping: next.attributeMapping,
      requireSso: next.requireSso,
      roleMapping: next.roleMapping,
      departmentMapping: next.departmentMapping,
      oidc: {
        issuer: next.oidc.issuer,
        clientId: next.oidc.clientId,
        clientSecret: oidcClientSecret.trim(), // only sent if the admin pasted a new one
        scopes: next.oidc.scopes,
        tokenAuthMethod: next.oidc.tokenAuthMethod,
        claimMapping: next.oidc.claimMapping,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.success) {
          setError(data?.message || "Could not save SSO settings.");
          return false;
        }
        setConfig((c) => ({
          ...c,
          ...next,
          idpCertificateMasked: idpCert.trim() ? "•••• (saved)" : c.idpCertificateMasked,
          oidc: {
            ...next.oidc,
            clientSecretMasked: oidcClientSecret.trim() ? "•••• (saved)" : next.oidc.clientSecretMasked,
          },
        }));
        setIdpCert("");
        setOidcClientSecret("");
        setSuccessMsg("Saved.");
        return true;
      })
      .finally(() => setSaving(false));
  }, [config, idpCert, oidcClientSecret]);

  // Switching protocol also pauses enforcement (enabled: false) so the switch itself always
  // succeeds — the backend's enable-gate would otherwise reject it outright if the
  // just-switched-to protocol isn't fully configured yet. The admin explicitly re-enables once
  // they've filled in that protocol's fields, same as any first-time setup.
  const switchProtocol = useCallback((protocol) => {
    if (protocol === config.protocol) return;
    save({ protocol, enabled: false });
  }, [config.protocol, save]);

  const addRoleMappingRow = useCallback(() => {
    setConfig((c) => ({ ...c, roleMapping: [...c.roleMapping, { idpValue: "", role: "employee", roleTemplateId: "" }] }));
  }, []);

  const updateRoleMappingRow = useCallback((index, field, value) => {
    setConfig((c) => ({
      ...c,
      roleMapping: c.roleMapping.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    }));
  }, []);

  // The Role mapping <select> below combines the 5 built-in roles with the tenant's own custom
  // templates into one list — a row's selection is either `role` (built-in) or `roleTemplateId`
  // (custom), never both, so the dropdown's single value has to encode which kind it is. Custom
  // template options are valued "template:<id>"; parsed back apart on change.
  const roleMappingSelectValue = (row) => (row.roleTemplateId ? `template:${row.roleTemplateId}` : row.role);
  const updateRoleMappingSelection = useCallback((index, selectedValue) => {
    const isTemplate = selectedValue.startsWith("template:");
    setConfig((c) => ({
      ...c,
      roleMapping: c.roleMapping.map((row, i) => (i === index
        ? {
            ...row,
            role: isTemplate ? "" : selectedValue,
            roleTemplateId: isTemplate ? selectedValue.slice("template:".length) : "",
          }
        : row)),
    }));
  }, []);

  const removeRoleMappingRow = useCallback((index) => {
    setConfig((c) => ({ ...c, roleMapping: c.roleMapping.filter((_, i) => i !== index) }));
  }, []);

  const addDepartmentMappingRow = useCallback(() => {
    setConfig((c) => ({
      ...c,
      departmentMapping: [...c.departmentMapping, { idpGroup: "", departmentId: departments[0]?._id || "" }],
    }));
  }, [departments]);

  const updateDepartmentMappingRow = useCallback((index, field, value) => {
    setConfig((c) => ({
      ...c,
      departmentMapping: c.departmentMapping.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    }));
  }, []);

  const removeDepartmentMappingRow = useCallback((index) => {
    setConfig((c) => ({ ...c, departmentMapping: c.departmentMapping.filter((_, i) => i !== index) }));
  }, []);

  const toggleRequireSso = useCallback((checked) => {
    if (checked && !window.confirm(
      "Turning this on disables password login for everyone at your organization immediately " +
      "(except the platform super-admin). Make sure sign-in via your identity provider works " +
      "before continuing."
    )) {
      return;
    }
    save({ requireSso: checked });
  }, [save]);

  if (loading) {
    return <div className="sso-page"><Loader2 className="sso-spin" size={22} /></div>;
  }

  return (
    <div className="sso-page">
      <p className="sso-subtitle">
        Connect Visun to your Identity Provider (e.g., Okta, Azure AD) for Single Sign-On (SSO).
        Once configured, users can log in directly from your provider's dashboard.
      </p>

      {error      && <div className="sso-banner sso-banner-error">{error}</div>}
      {successMsg && <div className="sso-banner sso-banner-success">{successMsg}</div>}

      <div className="sso-card">
        <div className="sso-field">
          <label>Protocol</label>
          <div className="sso-protocol-toggle">
            <button type="button" className={`sso-protocol-btn ${config.protocol === "saml" ? "active" : ""}`}
              disabled={!canManage || saving} onClick={() => switchProtocol("saml")}>
              SAML
              {config.protocol === "saml" && <span className="sso-badge sso-badge-on">Active</span>}
            </button>
            <button type="button" className={`sso-protocol-btn ${config.protocol === "oidc" ? "active" : ""}`}
              disabled={!canManage || saving} onClick={() => switchProtocol("oidc")}>
              OpenID Connect
              {config.protocol === "oidc" && <span className="sso-badge sso-badge-on">Active</span>}
            </button>
          </div>
          <p className="sso-hint">
            Settings for both protocols are saved independently. Switching protocols will pause SSO enforcement until re-enabled.
          </p>
        </div>
      </div>

      {config.protocol === "saml" && (
      <>
      {/* What to give the IdP */}
      <div className="sso-card">
        <h3 className="sso-card-title">1. Configure your Identity Provider (IdP)</h3>
        <p className="sso-card-hint">
          Configure your IdP app settings using the following Service Provider (SP) values.
        </p>
        <div className="sso-field">
          <label>Visun SP Entity ID</label>
          <div className="sso-copy-row">
            <input className="sso-input" readOnly value={config.spEntityId} />
            <button className="sso-btn-icon" onClick={() => copyToClipboard(config.spEntityId)} type="button"><Copy size={13} /></button>
          </div>
        </div>
        <div className="sso-field">
          <label>Assertion Consumer Service (ACS) URL</label>
          <div className="sso-copy-row">
            <input className="sso-input" readOnly value={config.acsUrl} />
            <button className="sso-btn-icon" onClick={() => copyToClipboard(config.acsUrl)} type="button"><Copy size={13} /></button>
          </div>
          <p className="sso-hint">Keep this URL private.</p>
        </div>
        <div className="sso-field">
          <label>SP metadata (XML)</label>
          <div className="sso-copy-row">
            <input className="sso-input" readOnly value={config.spMetadataUrl} />
            <a className="sso-btn-icon" href={config.spMetadataUrl} target="_blank" rel="noreferrer">Download</a>
          </div>
        </div>
      </div>

      {/* What to get back from the IdP */}
      <div className="sso-card">
        <h3 className="sso-card-title">
          2. Enter Identity Provider (IdP) details <span className={`sso-badge ${config.enabled ? "sso-badge-on" : "sso-badge-off"}`}>{config.enabled ? "ON" : "OFF"}</span>
        </h3>
        <p className="sso-card-hint">
          Paste the SAML values provided by your Identity Provider below.
        </p>
        <div className="sso-field">
          <label>IdP Entity ID</label>
          <input className="sso-input" value={config.idpEntityId} disabled={!canManage || saving}
            onChange={(e) => setConfig((c) => ({ ...c, idpEntityId: e.target.value }))} />
        </div>
        <div className="sso-field">
          <label>IdP SSO URL</label>
          <input className="sso-input" value={config.idpSsoUrl} disabled={!canManage || saving}
            onChange={(e) => setConfig((c) => ({ ...c, idpSsoUrl: e.target.value }))} />
        </div>
        <div className="sso-field">
          <label>IdP Signing Certificate {config.idpCertificateMasked && <em>(Saved)</em>}</label>
          <textarea className="sso-textarea" placeholder="-----BEGIN CERTIFICATE-----" value={idpCert}
            disabled={!canManage || saving} onChange={(e) => setIdpCert(e.target.value)} />
          <p className="sso-hint">Leave blank to keep the current certificate.</p>
        </div>
        <div className="sso-field">
          <label>Attribute mapping</label>
          <div className="sso-copy-row" style={{ marginBottom: "0.5rem" }}>
            <input className="sso-input" placeholder="Email attribute name" value={config.attributeMapping.email}
              disabled={!canManage || saving}
              onChange={(e) => setConfig((c) => ({ ...c, attributeMapping: { ...c.attributeMapping, email: e.target.value } }))} />
            <input className="sso-input" placeholder="Name attribute name" value={config.attributeMapping.name}
              disabled={!canManage || saving}
              onChange={(e) => setConfig((c) => ({ ...c, attributeMapping: { ...c.attributeMapping, name: e.target.value } }))} />
          </div>
          <p className="sso-hint">Map SAML assertions to user profile fields (defaults: "email", "name").</p>
        </div>
        {canManage && (
          <>
            <button className="sso-btn sso-btn-primary" disabled={saving} onClick={() => save()}>
              {saving && <Loader2 className="sso-spin-inline" size={14} />} Save
            </button>
            <label className="sso-row" style={{ marginTop: "0.75rem" }}>
              <span>Enable SSO for this organization</span>
              <span className="inline-toggle">
                <input type="checkbox" checked={config.enabled} disabled={saving}
                  onChange={(e) => save({ enabled: e.target.checked })} />
                <span className="toggle-slider"></span>
              </span>
            </label>
          </>
        )}
      </div>
      </>
      )}

      {config.protocol === "oidc" && (
      <>
      {/* What to give the IdP */}
      <div className="sso-card">
        <h3 className="sso-card-title">1. Register with your Identity Provider (IdP)</h3>
        <p className="sso-card-hint">
          Register Visun in your IdP app settings using the redirect URI below. Set the Login URL as your IdP app tile's target.
        </p>
        <div className="sso-field">
          <label>Redirect URI</label>
          <div className="sso-copy-row">
            <input className="sso-input" readOnly value={config.oidcRedirectUri} />
            <button className="sso-btn-icon" onClick={() => copyToClipboard(config.oidcRedirectUri)} type="button"><Copy size={13} /></button>
          </div>
          <p className="sso-hint">Keep this URI private.</p>
        </div>
        <div className="sso-field">
          <label>Login URL</label>
          <div className="sso-copy-row">
            <input className="sso-input" readOnly value={config.oidcLoginUrl} />
            <button className="sso-btn-icon" onClick={() => copyToClipboard(config.oidcLoginUrl)} type="button"><Copy size={13} /></button>
          </div>
        </div>
      </div>

      {/* What to get back from the IdP */}
      <div className="sso-card">
        <h3 className="sso-card-title">
          2. Enter Identity Provider (IdP) details <span className={`sso-badge ${config.enabled ? "sso-badge-on" : "sso-badge-off"}`}>{config.enabled ? "ON" : "OFF"}</span>
        </h3>
        <p className="sso-card-hint">
          Paste the OIDC credentials provided by your Identity Provider below.
        </p>
        <div className="sso-field">
          <label>Issuer URL</label>
          <input className="sso-input" placeholder="https://your-idp.example.com" value={config.oidc.issuer} disabled={!canManage || saving}
            onChange={(e) => setConfig((c) => ({ ...c, oidc: { ...c.oidc, issuer: e.target.value } }))} />
          <p className="sso-hint">Visun fetches endpoint configurations from this URL.</p>
        </div>
        <div className="sso-field">
          <label>Client ID</label>
          <input className="sso-input" value={config.oidc.clientId} disabled={!canManage || saving}
            onChange={(e) => setConfig((c) => ({ ...c, oidc: { ...c.oidc, clientId: e.target.value } }))} />
        </div>
        <div className="sso-field">
          <label>Client Secret {config.oidc.clientSecretMasked && <em>(Saved)</em>}</label>
          <input className="sso-input" type="password" placeholder="Leave blank to keep the current secret" value={oidcClientSecret}
            disabled={!canManage || saving} onChange={(e) => setOidcClientSecret(e.target.value)} />
        </div>
        <div className="sso-field">
          <label>Scopes</label>
          <input className="sso-input" value={config.oidc.scopes} disabled={!canManage || saving}
            onChange={(e) => setConfig((c) => ({ ...c, oidc: { ...c.oidc, scopes: e.target.value } }))} />
        </div>
        <div className="sso-field">
          <label>Token endpoint authentication</label>
          <select className="sso-select" value={config.oidc.tokenAuthMethod} disabled={!canManage || saving}
            onChange={(e) => setConfig((c) => ({ ...c, oidc: { ...c.oidc, tokenAuthMethod: e.target.value } }))}>
            <option value="client_secret_basic">Client Secret Basic (default)</option>
            <option value="client_secret_post">Client Secret Post</option>
          </select>
          <p className="sso-hint">Authentication method for requesting tokens (default: Basic).</p>
        </div>
        <div className="sso-field">
          <label>Claim mapping</label>
          <div className="sso-copy-row" style={{ marginBottom: "0.5rem" }}>
            <input className="sso-input" placeholder="Email claim name" value={config.oidc.claimMapping.email}
              disabled={!canManage || saving}
              onChange={(e) => setConfig((c) => ({ ...c, oidc: { ...c.oidc, claimMapping: { ...c.oidc.claimMapping, email: e.target.value } } }))} />
            <input className="sso-input" placeholder="Name claim name" value={config.oidc.claimMapping.name}
              disabled={!canManage || saving}
              onChange={(e) => setConfig((c) => ({ ...c, oidc: { ...c.oidc, claimMapping: { ...c.oidc.claimMapping, name: e.target.value } } }))} />
          </div>
          <p className="sso-hint">Map OIDC claims to user profile fields (defaults: "email", "name").</p>
        </div>
        {canManage && (
          <>
            <button className="sso-btn sso-btn-primary" disabled={saving} onClick={() => save()}>
              {saving && <Loader2 className="sso-spin-inline" size={14} />} Save
            </button>
            <label className="sso-row" style={{ marginTop: "0.75rem" }}>
              <span>Enable SSO for this organization</span>
              <span className="inline-toggle">
                <input type="checkbox" checked={config.enabled} disabled={saving}
                  onChange={(e) => save({ enabled: e.target.checked })} />
                <span className="toggle-slider"></span>
              </span>
            </label>
          </>
        )}
      </div>
      </>
      )}

      {/* Role mapping — shared by both protocols, matched against whichever attribute/claim name
          is set below for the currently selected protocol. */}
      <div className="sso-card">
        <h3 className="sso-card-title">Role mapping</h3>
        <p className="sso-card-hint">
          Map your Identity Provider's role/group values to Visun roles. The first matching rule applies. Users with matched roles bypass manual approval and sign in instantly. Unmatched users are held for admin approval.
        </p>
        <div className="sso-field">
          <label>Role {config.protocol === "oidc" ? "claim" : "attribute"} name</label>
          <input className="sso-input" placeholder="e.g. Role"
            value={config.protocol === "oidc" ? config.oidc.claimMapping.role : config.attributeMapping.role}
            disabled={!canManage || saving}
            onChange={(e) => {
              const value = e.target.value;
              setConfig((c) => c.protocol === "oidc"
                ? { ...c, oidc: { ...c.oidc, claimMapping: { ...c.oidc.claimMapping, role: value } } }
                : { ...c, attributeMapping: { ...c.attributeMapping, role: value } });
            }} />
          <p className="sso-hint">
            The {config.protocol === "oidc" ? "OIDC claim" : "SAML attribute"} containing user
            roles. Leave blank to manually approve new users.
          </p>
        </div>
        {config.roleMapping.length > 0 && !((config.protocol === "oidc" ? config.oidc.claimMapping.role : config.attributeMapping.role) || "").trim() && (
          <p className="sso-hint" style={{ color: "var(--color-warning)" }}>
            You have mapping rows below, but no attribute/claim name is set above — they'll be
            ignored until you fill it in.
          </p>
        )}
        <div className="sso-match-box">
          {config.roleMapping.length > 0 && (
            <table className="sso-match-table">
              <thead>
                <tr>
                  <th>IdP value</th>
                  <th aria-hidden="true"></th>
                  <th>Visun role</th>
                  <th aria-hidden="true"></th>
                </tr>
              </thead>
              <tbody>
                {config.roleMapping.map((row, i) => (
                  <tr key={i}>
                    <td>
                      <input className="sso-input" placeholder="e.g. cfo-role" value={row.idpValue}
                        disabled={!canManage || saving}
                        onChange={(e) => updateRoleMappingRow(i, "idpValue", e.target.value)} />
                    </td>
                    <td className="sso-match-arrow" aria-hidden="true">→</td>
                    <td>
                      <select className="sso-select" value={roleMappingSelectValue(row)} disabled={!canManage || saving}
                        onChange={(e) => updateRoleMappingSelection(i, e.target.value)}>
                        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        {selectableRoleTemplates.map((t) => (
                          <option key={t._id} value={`template:${t._id}`}>{t.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {canManage && (
                        <button className="sso-btn-icon sso-match-remove" type="button" onClick={() => removeRoleMappingRow(i)} title="Remove row">
                          <X size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {config.roleMapping.length === 0 && (
            <p className="sso-match-empty">No mappings configured. All new SSO users will require manual approval.</p>
          )}
          {canManage && (
            <button className="sso-btn sso-btn-secondary sso-match-add" type="button" disabled={saving} onClick={addRoleMappingRow}>
              <Plus size={13} /> Add row
            </button>
          )}
        </div>
        {canManage && (
          <button className="sso-btn sso-btn-primary" disabled={saving} onClick={() => save()}>
            {saving && <Loader2 className="sso-spin-inline" size={14} />} Save role mapping
          </button>
        )}
      </div>

      {/* Department mapping — shared by both protocols, matched against whichever group
          attribute/claim name is set below. Manager/employee only: an employee's device link
          still needs a tenant admin (or, later, self-enrollment) to actually happen, since no IdP
          claim can identify which physical machine belongs to them — this only resolves *which
          team* they belong to, ahead of that. */}
      <div className="sso-card">
        <h3 className="sso-card-title">Department mapping</h3>
        <p className="sso-card-hint">
          Map your Identity Provider's group values to departments/teams. Every matching row applies
          (a manager can own more than one team). Matched managers skip manual department
          assignment; unmatched managers are still held for a tenant admin to link one.
        </p>
        <div className="sso-field">
          <label>Department/group {config.protocol === "oidc" ? "claim" : "attribute"} name</label>
          <input className="sso-input" placeholder="e.g. groups"
            value={config.protocol === "oidc" ? config.oidc.claimMapping.group : config.attributeMapping.group}
            disabled={!canManage || saving}
            onChange={(e) => {
              const value = e.target.value;
              setConfig((c) => c.protocol === "oidc"
                ? { ...c, oidc: { ...c.oidc, claimMapping: { ...c.oidc.claimMapping, group: value } } }
                : { ...c, attributeMapping: { ...c.attributeMapping, group: value } });
            }} />
          <p className="sso-hint">
            The {config.protocol === "oidc" ? "OIDC claim" : "SAML attribute"} carrying team/group
            membership (e.g. Keycloak's Group Membership mapper). Leave blank to assign departments
            manually instead.
          </p>
        </div>
        {config.departmentMapping.length > 0 && !((config.protocol === "oidc" ? config.oidc.claimMapping.group : config.attributeMapping.group) || "").trim() && (
          <p className="sso-hint" style={{ color: "var(--color-warning)" }}>
            You have mapping rows below, but no attribute/claim name is set above — they'll be
            ignored until you fill it in.
          </p>
        )}
        <div className="sso-match-box">
          {config.departmentMapping.length > 0 && (
            <table className="sso-match-table">
              <thead>
                <tr>
                  <th>IdP group</th>
                  <th aria-hidden="true"></th>
                  <th>Department</th>
                  <th aria-hidden="true"></th>
                </tr>
              </thead>
              <tbody>
                {config.departmentMapping.map((row, i) => (
                  <tr key={i}>
                    <td>
                      <input className="sso-input" placeholder="e.g. /Sales" value={row.idpGroup}
                        disabled={!canManage || saving}
                        onChange={(e) => updateDepartmentMappingRow(i, "idpGroup", e.target.value)} />
                    </td>
                    <td className="sso-match-arrow" aria-hidden="true">→</td>
                    <td>
                      <select className="sso-select" value={row.departmentId} disabled={!canManage || saving}
                        onChange={(e) => updateDepartmentMappingRow(i, "departmentId", e.target.value)}>
                        <option value="">Select department…</option>
                        {departments.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td>
                      {canManage && (
                        <button className="sso-btn-icon sso-match-remove" type="button" onClick={() => removeDepartmentMappingRow(i)} title="Remove row">
                          <X size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {config.departmentMapping.length === 0 && (
            <p className="sso-match-empty">No mappings configured. Managers will require manual department assignment.</p>
          )}
          {canManage && (
            <button className="sso-btn sso-btn-secondary sso-match-add" type="button" disabled={saving || departments.length === 0} onClick={addDepartmentMappingRow}>
              <Plus size={13} /> Add row
            </button>
          )}
          {canManage && departments.length === 0 && (
            <p className="sso-hint">Create a department first (Devices → Departments) before adding rows here.</p>
          )}
        </div>
        {canManage && (
          <button className="sso-btn sso-btn-primary" disabled={saving} onClick={() => save()}>
            {saving && <Loader2 className="sso-spin-inline" size={14} />} Save department mapping
          </button>
        )}
      </div>

      {/* General */}
      <div className="sso-card sso-danger-zone">
        <h3 className="sso-card-title"><ShieldCheck size={16} /> Enforcement</h3>
        <label className="sso-row">
          <span>Require SSO (disable password login for this organization)</span>
          <span className="inline-toggle">
            <input type="checkbox" checked={config.requireSso} disabled={!canManage || saving}
              onChange={(e) => toggleRequireSso(e.target.checked)} />
            <span className="toggle-slider"></span>
          </span>
        </label>
        <p className="sso-hint">SSO enforcement takes effect immediately. The platform super-admin is exempt.</p>
      </div>
    </div>
  );
}
